import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { type IntegrationActivity } from '@/api/routes/partners/partners.integration.api';
import { formatIntegrationCount, totalClicks } from '@/domain/integration/utils';
import { useTranslate } from '@/state/i18n';
import { radiusTokens } from '@/theme/themePrimitives';

interface Props {
  partnerName: string;
  subjectsAvailable: number;
  activity: IntegrationActivity;
}

/** P17 numbers. Counts only: the portal never shows who the users are. */
const IntegrationIndicators = ({ partnerName, subjectsAvailable, activity }: Props) => {
  const t = useTranslate('domain.integration.components.IntegrationIndicators');

  const items = [
    {
      label: t('subjects'),
      value: subjectsAvailable,
      hint: t('subjectsHint', { name: partnerName }),
    },
    { label: t('media'), value: activity.mediaReceived, hint: t('mediaHint') },
    {
      label: t('notifications'),
      value: activity.notificationsDelivered,
      hint: t('notificationsHint'),
    },
    {
      label: t('clicks', { name: partnerName }),
      value: totalClicks(activity),
      hint: t('clicksHint', {
        gallery: formatIntegrationCount(activity.galleryClicks),
        media: formatIntegrationCount(activity.mediaClicks),
      }),
    },
  ];

  return (
    <Box sx={sx.grid}>
      {items.map((item) => (
        <Paper key={item.label} variant="outlined" sx={sx.card}>
          <Typography variant="caption" color="text.secondary">
            {item.label}
          </Typography>
          <Typography variant="h3" component="p">
            {formatIntegrationCount(item.value)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {item.hint}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
};

export default IntegrationIndicators;

const sx = {
  grid: {
    display: 'grid',
    gap: 2,
    gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
  },
  card: {
    borderRadius: radiusTokens.xxl,
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
  },
};
